import { useMemo } from 'react';
import styled from 'styled-components';
import type { ContextualTip, TipTokenPrefix, TipTour } from './types';

type TipsPanelProps = {
  tokenPrefix: TipTokenPrefix;
  open: boolean;
  onClose: () => void;
  tips: ContextualTip[];
  tours: TipTour[];
  isTourDone: (tourId: string, version: number) => boolean;
  onReplayTour: (tourId: string) => void;
  onStartTour: (tourId: string) => void;
};

const Backdrop = styled.button`
  position: fixed;
  inset: 0;
  z-index: 70;
  margin: 0;
  padding: 0;
  border: none;
  background: rgba(16, 21, 28, 0.28);
  cursor: default;
`;

const Panel = styled.aside<{ $prefix: TipTokenPrefix }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 71;
  width: min(380px, 100vw);
  background: var(--${({ $prefix }) => $prefix}-paper);
  border-left: 1px solid var(--${({ $prefix }) => $prefix}-hairline);
  box-shadow: var(--${({ $prefix }) => $prefix}-shadow-overlay);
  display: flex;
  flex-direction: column;
  font-family: var(--${({ $prefix }) => $prefix}-font-ui);
  color: var(--${({ $prefix }) => $prefix}-ink);
`;

const Head = styled.div<{ $prefix: TipTokenPrefix }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--${({ $prefix }) => $prefix}-hairline);
`;

const HeadCopy = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.p<{ $prefix: TipTokenPrefix }>`
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--${({ $prefix }) => $prefix}-muted);
`;

const Title = styled.h2<{ $prefix: TipTokenPrefix }>`
  margin: 0;
  font-family: var(--${({ $prefix }) => $prefix}-font-display);
  font-size: 20px;
  font-weight: 700;
`;

const CloseBtn = styled.button<{ $prefix: TipTokenPrefix }>`
  appearance: none;
  border: 1px solid var(--${({ $prefix }) => $prefix}-hairline);
  background: var(--${({ $prefix }) => $prefix}-paper);
  color: var(--${({ $prefix }) => $prefix}-ink);
  width: 32px;
  height: 32px;
  border-radius: var(--${({ $prefix }) => $prefix}-r-input);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionLabel = styled.h3<{ $prefix: TipTokenPrefix }>`
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--${({ $prefix }) => $prefix}-muted);
`;

const Card = styled.div<{ $prefix: TipTokenPrefix }>`
  border: 1px solid var(--${({ $prefix }) => $prefix}-hairline);
  border-radius: var(--${({ $prefix }) => $prefix}-r-card);
  background: var(--${({ $prefix }) => $prefix}-canvas);
  padding: 12px 14px;
`;

const CardTitle = styled.p<{ $prefix: TipTokenPrefix }>`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 700;
  color: var(--${({ $prefix }) => $prefix}-ink);
`;

const CardBody = styled.p<{ $prefix: TipTokenPrefix }>`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--${({ $prefix }) => $prefix}-muted);
`;

const CardActions = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 8px;
`;

const TextBtn = styled.button<{ $prefix: TipTokenPrefix }>`
  appearance: none;
  border: 1px solid var(--${({ $prefix }) => $prefix}-hairline);
  background: var(--${({ $prefix }) => $prefix}-paper);
  color: var(--${({ $prefix }) => $prefix}-ink);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: var(--${({ $prefix }) => $prefix}-r-input);
  cursor: pointer;

  &:hover {
    border-color: var(--${({ $prefix }) => $prefix}-navy);
  }
`;

export const TipsPanel = ({
  tokenPrefix,
  open,
  onClose,
  tips,
  tours,
  isTourDone,
  onReplayTour,
  onStartTour,
}: TipsPanelProps) => {
  const tipSections = useMemo(() => {
    const map = new Map<string, ContextualTip[]>();
    for (const tip of tips) {
      const list = map.get(tip.section) ?? [];
      list.push(tip);
      map.set(tip.section, list);
    }
    return Array.from(map.entries());
  }, [tips]);

  if (!open) return null;

  return (
    <>
      <Backdrop type="button" aria-label="Close tips" onClick={onClose} />
      <Panel
        $prefix={tokenPrefix}
        role="dialog"
        aria-modal="true"
        aria-label="Tips"
      >
        <Head $prefix={tokenPrefix}>
          <HeadCopy>
            <Eyebrow $prefix={tokenPrefix}>Guide</Eyebrow>
            <Title $prefix={tokenPrefix}>Tips</Title>
          </HeadCopy>
          <CloseBtn
            type="button"
            $prefix={tokenPrefix}
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </CloseBtn>
        </Head>
        <Body>
          {tours.length > 0 && (
            <Section>
              <SectionLabel $prefix={tokenPrefix}>Walkthroughs</SectionLabel>
              {tours.map((tour) => {
                const done = isTourDone(tour.id, tour.version);
                return (
                  <Card key={tour.id} $prefix={tokenPrefix}>
                    <CardTitle $prefix={tokenPrefix}>{tour.title}</CardTitle>
                    {tour.description && (
                      <CardBody $prefix={tokenPrefix}>
                        {tour.description}
                      </CardBody>
                    )}
                    <CardActions>
                      <TextBtn
                        type="button"
                        $prefix={tokenPrefix}
                        onClick={() =>
                          done ? onReplayTour(tour.id) : onStartTour(tour.id)
                        }
                      >
                        {done ? 'Replay' : 'Start'}
                      </TextBtn>
                    </CardActions>
                  </Card>
                );
              })}
            </Section>
          )}

          {tipSections.map(([section, items]) => (
            <Section key={section}>
              <SectionLabel $prefix={tokenPrefix}>{section}</SectionLabel>
              {items.map((tip) => (
                <Card key={tip.id} $prefix={tokenPrefix}>
                  <CardTitle $prefix={tokenPrefix}>{tip.title}</CardTitle>
                  <CardBody $prefix={tokenPrefix}>{tip.body}</CardBody>
                </Card>
              ))}
            </Section>
          ))}
        </Body>
      </Panel>
    </>
  );
};

export default TipsPanel;
